import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import { isValidLocaleSegment, normalizeLocaleForTranslations } from "@/utils/localeRouteHelpers";
import { getCachedSupportedLocales } from "@/services/supportedLocalesService";

interface ITranslations {
  [key: string]: string | ITranslations;
}

const cachedTranslations: Record<string, ITranslations> = {};
const loadingStates: Record<string, "loading" | "loaded" | "error"> = {};
const loadingCallbacks: Record<string, Array<(data: ITranslations | null) => void>> = {};

function resolveLocale(routeLocale: string | undefined): string {
  const normalizedRouteLocale = normalizeLocaleForTranslations(routeLocale);
  if (normalizedRouteLocale && isValidLocaleSegment(normalizedRouteLocale)) {
    const supportedLocales = getCachedSupportedLocales();
    if (!supportedLocales || supportedLocales.has(normalizedRouteLocale)) {
      return normalizedRouteLocale;
    }
    // URL locale is format-valid but not in the supported set — use default
    return "en-us";
  }

  const storedLocale = normalizeLocaleForTranslations(getLocalePreference()?.localeCode);
  if (storedLocale && isValidLocaleSegment(storedLocale)) {
    return storedLocale;
  }

  return "en-us";
}

async function loadTranslationsGlobal(locale: string): Promise<ITranslations | null> {
  // Already cached
  if (cachedTranslations[locale]) {
    return cachedTranslations[locale];
  }

  // Currently loading - register callback
  if (loadingStates[locale] === "loading") {
    // eslint-disable-next-line custom/explicit-return-types
    return new Promise((resolve) => {
      if (!loadingCallbacks[locale]) {
        loadingCallbacks[locale] = [];
      }
      loadingCallbacks[locale].push(resolve);
    });
  }

  // Start loading
  loadingStates[locale] = "loading";

  try {
    const response = await fetch(`/locales/${locale}.translations.json`);

    if (!response.ok) {
      if (locale !== "en-us") {
        const fallbackResponse = await fetch("/locales/en-us.translations.json");
        if (fallbackResponse.ok) {
          const fallbackData = (await fallbackResponse.json()) as ITranslations;
          cachedTranslations[locale] = fallbackData;
          loadingStates[locale] = "loaded";

          // Notify waiting callbacks
          loadingCallbacks[locale]?.forEach((cb) => cb(fallbackData));
          delete loadingCallbacks[locale];

          return fallbackData;
        }
      }
      loadingStates[locale] = "error";

      // Notify waiting callbacks
      loadingCallbacks[locale]?.forEach((cb) => cb(null));
      delete loadingCallbacks[locale];

      return null;
    }

    const data = (await response.json()) as ITranslations;
    cachedTranslations[locale] = data;
    loadingStates[locale] = "loaded";

    // Notify waiting callbacks
    loadingCallbacks[locale]?.forEach((cb) => cb(data));
    delete loadingCallbacks[locale];

    return data;
  } catch (err) {
    console.error("Failed to load translations:", err);
    loadingStates[locale] = "error";

    // Notify waiting callbacks
    loadingCallbacks[locale]?.forEach((cb) => cb(null));
    delete loadingCallbacks[locale];

    return null;
  }
}

/**
 * Custom hook to load and access translations based on the current locale
 * @returns Object containing translations, loading state, and helper function to get nested translations
 */
export function useTranslations(): {
  t: (key: string, replacements?: Record<string, string>) => string;
  translations: ITranslations | null;
  isLoading: boolean;
} {
  const params = useParams();
  const locale = resolveLocale(params?.locale as string | undefined);

  // Use cached translations immediately if available
  const [translations, setTranslations] = useState<ITranslations | null>(
    cachedTranslations[locale] || null
  );
  const [isLoading, setIsLoading] = useState(!cachedTranslations[locale]);

  useEffect(() => {
    let isMounted = true;

    // Load translations
    loadTranslationsGlobal(locale)
      .then((data) => {
        if (isMounted && data) {
          setTranslations(data);
          setIsLoading(false);
        } else if (isMounted) {
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.error("Failed to load translations:", err);
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return (): void => {
      isMounted = false;
    };
  }, [locale]);

  /**
   * Get a translated string by key path (e.g., "navigation.filterByTitle")
   * @param key Dot-separated path to the translation
   * @param replacements Optional object with placeholder replacements (e.g., {userName: "John"})
   * @returns Translated string or the key itself if not found
   */
  const t = useCallback(
    (key: string, replacements?: Record<string, string>): string => {
      if (!translations) {
        return key;
      }

      const keys = key.split(".");
      let value: string | ITranslations | undefined = translations;

      for (const k of keys) {
        if (typeof value === "object" && value !== null && k in value) {
          value = value[k];
        } else {
          return key; // Return key if path not found
        }
      }

      if (typeof value !== "string") {
        return key;
      }

      // Replace placeholders like {userName} with actual values
      if (replacements) {
        return value.replace(/\{(\w+)\}/g, (match, placeholder) => {
          return replacements[placeholder] || match;
        });
      }

      return value;
    },
    [translations]
  );

  return { t, translations, isLoading };
}

/**
 * Clear the translations cache (useful when locale changes)
 */
export function clearTranslationsCache(): void {
  Object.keys(cachedTranslations).forEach((key) => delete cachedTranslations[key]);
  Object.keys(loadingStates).forEach((key) => delete loadingStates[key]);
  Object.keys(loadingCallbacks).forEach((key) => delete loadingCallbacks[key]);
}
